import React, { useEffect, useState } from "react";
import { Typography, Grid, Link } from "@mui/material";
import ResponsiveButton from "../../../../../Forms/ResponsiveButton";
import DeviceSelector from "./DeviceSelector";
import RegionSelector from "./RegionSelector";
import VectorTypeSelector from "./VectorTypeSelector";
import { getVectorTypeGivenRegion } from "../../../../../../utils/helpers";

const fontSx = {
  fontSize: [
    "0.75rem",
    "0.75rem",
    "0.75rem",
    "0.75rem",
    "1.125rem",
    "1.125rem",
  ],
};

const DeviceInfoPanel = ({
  devices,
  onUpdateDeviceVectorType,
  deviceUuid,
  onUpdateDeviceUuid,
  displayInfoNotification,
  possibleVectorTypes,
  initialRegion,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedVectorTypeValue, setSelectedVectorTypeValue] = useState("");
  const [selectedRegionValue, setSelectedRegionValue] = useState("");
  const [regions, setRegions] = useState({});

  // save is disabled when not dirty
  const saveDisabled =
    !isEditing ||
    (selectedRegionValue || "").length === 0 ||
    selectedRegionValue === initialRegion;

  const handleSaveButtonPressed = () => {
    if (selectedRegionValue.length > 0) {
      // check if vectorType changed
      const oldVectorType =
        initialRegion === undefined || initialRegion.startsWith("moz")
          ? "mosquito"
          : initialRegion.startsWith("tick")
            ? "tick"
            : initialRegion;
      const newVectorType =
        selectedRegionValue === undefined ||
          selectedRegionValue.startsWith("moz")
          ? "mosquito"
          : selectedRegionValue.startsWith("tick")
            ? "tick"
            : selectedRegionValue;
      if (oldVectorType !== newVectorType) {
        // if device requires restart, let user know
        if (displayInfoNotification) {
          displayInfoNotification(
            `Changing the Vector Type. Device will restart to apply changes...`,
            { vertical: "top", horizontal: "center" }
          );
        }
      }
      onUpdateDeviceVectorType(selectedRegionValue);
    }
    setIsEditing(false);
  };

  const handleToggleEdit = () => {
    if (isEditing) {
      // handle CANCEL
      setIsEditing(false);
      const initialVectorType = getVectorTypeGivenRegion(initialRegion);
      setSelectedRegionValue(initialRegion);
      setSelectedVectorTypeValue(initialVectorType);
    } else {
      // handle EDIT
      setIsEditing(true);
    }
  };

  // if selectedVectorType changes, update regions
  useEffect(() => {
    setRegions(possibleVectorTypes?.[selectedVectorTypeValue] || {});
  }, [possibleVectorTypes, selectedVectorTypeValue, setRegions]);

  // if initial region changes, reset selectors
  useEffect(() => {
    const initialVectorType = getVectorTypeGivenRegion(initialRegion);
    setSelectedRegionValue(initialRegion);
    setSelectedVectorTypeValue(initialVectorType);
  }, [initialRegion, setSelectedRegionValue, setSelectedVectorTypeValue]);

  return (
    <Grid container rowSpacing={2} columnSpacing={1}>
      <DeviceSelector
        devices={devices}
        disabled={!isEditing}
        deviceUuid={deviceUuid}
        onDeviceChange={(s) => onUpdateDeviceUuid(s)}
      />

      <VectorTypeSelector
        disabled={!isEditing}
        selectedVectorType={selectedVectorTypeValue}
        onVectorTypeChange={setSelectedVectorTypeValue}
        possibleVectorTypes={possibleVectorTypes}
        initialRegion={initialRegion}
      />

      <RegionSelector
        disabled={!isEditing}
        selectedRegion={selectedRegionValue}
        initialRegion={initialRegion}
        regions={regions}
        onRegionChange={setSelectedRegionValue}
      />

      <Grid
        size={12}
        sx={{
          justifyContent: "flex-start",
          alignItems: "center",
          display: "flex",
        }}
      >
        <Link
          href="https://www.notion.so/Mosquito-Tick-Species-by-Region-1ed858760a4a804b80fbe1fa4a52d12c?pvs=4"
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
          sx={{
            ...fontSx,
            color: "#167bc3",
          }}
        >
          <Typography sx={fontSx}>Find My Region</Typography>
        </Link>
      </Grid>

      <Grid
        size={6}
        sx={{
          justifyContent: "flex-end",
          alignItems: "center",
          display: "flex",
          mt: "10px",
        }}
      >
        <ResponsiveButton
          variant="contained"
          style={{
            color: "#167bc3",
            backgroundColor: "white",
            borderColor: "rgba(63, 81, 181, 0.5)",
            border: "1px solid",
            boxShadow: "none",
          }}
          onClick={() => handleToggleEdit()}
        >
          {isEditing ? "CANCEL" : "EDIT"}
        </ResponsiveButton>
      </Grid>
      <Grid
        size={6}
        sx={{
          justifyContent: "flex-start",
          alignItems: "center",
          display: "flex",
          mt: "10px",
        }}
      >
        <ResponsiveButton
          variant="contained"
          style={{
            color: saveDisabled ? "gray" : "#167bc3",
            backgroundColor: "white",
            borderColor: saveDisabled ? "gray" : "rgba(63, 81, 181, 0.5)",
            border: "1px solid",
            boxShadow: "none",
            cursor: saveDisabled ? "not-allowed" : "pointer",
          }}
          disabled={saveDisabled}
          onClick={handleSaveButtonPressed}
        >
          SAVE
        </ResponsiveButton>
      </Grid>
    </Grid>
  );
};

export default DeviceInfoPanel;
